'use client';

import { motion } from 'framer-motion';
import type { PageSection } from '@/lib/cms';
import { HeroBanner } from '@/components/ui/HeroBanner';
import type { HeroBannerSettings } from '@/lib/cms';
import { CmsFeaturesSection } from './CmsFeaturesSection';
import { CmsTestimonialsSection } from './CmsTestimonialsSection';
import { CmsCtaSection } from './CmsCtaSection';
import { CmsStatsSection } from './CmsStatsSection';
import { sanitizeHtml } from '@/lib/sanitize-html';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

export function SectionRenderer({
  section,
  index,
}: {
  section: PageSection;
  index: number;
}) {
  const key = section.key ?? `${section.type}-${index}`;

  switch (section.type) {
    case 'hero_banner':
      return (
        <HeroBanner
          key={key}
          data={(section.settings ?? {}) as HeroBannerSettings}
        />
      );
    case 'features':
      return (
        <motion.div key={key} variants={item}>
          <CmsFeaturesSection section={section} />
        </motion.div>
      );
    case 'testimonials':
      return (
        <motion.div key={key} variants={item}>
          <CmsTestimonialsSection section={section} />
        </motion.div>
      );
    case 'cta':
      return (
        <motion.div key={key} variants={item}>
          <CmsCtaSection section={section} />
        </motion.div>
      );
    case 'stats':
      return (
        <motion.div key={key} variants={item}>
          <CmsStatsSection section={section} />
        </motion.div>
      );
    case 'categories':
    case 'pricing':
    case 'faq':
    case 'team':
    case 'gallery':
    case 'timeline':
    case 'sponsors':
    case 'reviews':
      return (
        <motion.section
          key={key}
          variants={item}
          className="py-16 px-4 sm:px-6 lg:px-8 bg-plaster"
        >
          <div className="max-w-4xl mx-auto text-center">
            {section.title && (
              <h2 className="text-2xl font-bold text-soot">{section.title}</h2>
            )}
            {section.content && (
              <p className="mt-2 text-moss">{section.content}</p>
            )}
            <p className="mt-4 text-landing-muted text-sm">
              Section type &quot;{section.type}&quot; — add custom component or use items from CMS.
            </p>
          </div>
        </motion.section>
      );
    default:
      if (section.title || section.content) {
        return (
          <motion.section
            key={key}
            variants={item}
            className="py-12 px-4 sm:px-6 lg:px-8 bg-bg"
          >
            <div className="max-w-4xl mx-auto">
              {section.title && (
                <h2 className="text-2xl font-bold text-landing-text">{section.title}</h2>
              )}
              {section.content && (
                <div
                  className="mt-4 prose prose-landing max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                />
              )}
            </div>
          </motion.section>
        );
      }
      return null;
  }
}

export function SectionsBlock({ sections }: { sections: PageSection[] }) {
  const toRender = sections.filter((s) => s.type !== 'hero_banner');
  if (toRender.length === 0) return null;

  return (
    <motion.div
      className="space-y-0"
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-50px' }}
    >
      {toRender.map((section, index) => (
        <SectionRenderer key={section.key ?? section.sort_order ?? index} section={section} index={index} />
      ))}
    </motion.div>
  );
}
